import { Box } from '@mui/material';
import { useMemo } from 'react';
import type { PointerEvent } from 'react';

import {
  useBoardDragPreviewStore,
  useBoardElementRectStore,
  useBoardSelectionStore,
  useWorkspaceUiStore,
  type BoardElementId,
  type WallCard,
  type WallConnection,
} from '@walls/store';
import { isConnectionVisibleForParticipantFilter } from '../cards/utils/cardVisibility';
import {
  getCardDragPreviewRects,
  getConnectorPoints,
  getConnectionLinePoints,
  getConnectionPointLinePoints,
  getPointConnectorPoints,
  getRenderedConnections,
  type ConnectionLinePoints,
} from '../connections/connectionGeometry';
import { getBoardCardRectMap } from '../positioning/cardRects';
import { connectionLayerZIndex } from '../positioning/zIndex';
import { rectsIntersect, type BoardRenderWindow } from '../viewport/renderWindow';
import type { BoardRect } from '../selection/utils/selectionGeometry';

interface BoardConnectionsLayerProps {
  cards: WallCard[];
  connections: WallConnection[];
  renderWindow: BoardRenderWindow;
}

const focusFilteredConnectionOpacity = 0.12;

export function BoardConnectionsLayer({
  cards,
  connections,
  renderWindow,
}: BoardConnectionsLayerProps) {
  const connectingFromId = useBoardSelectionStore((state) => state.connectingFromId);
  const connectionPointer = useBoardSelectionStore((state) => state.connectionPointer);
  const currentElement = useBoardSelectionStore((state) => state.currentElement);
  const setCurrentElement = useBoardSelectionStore((state) => state.setCurrentElement);
  const setSelectedIds = useBoardSelectionStore((state) => state.setSelectedIds);
  const cardDragPreviews = useBoardDragPreviewStore((state) => state.cardDragPreviews);
  const measuredRects = useBoardElementRectStore((state) => state.rects);
  const selectedPenColor = useWorkspaceUiStore((state) => state.selectedPenColor);
  const visibleParticipantIds = useWorkspaceUiStore((state) => state.visibleParticipantIds);
  const dragPreviewRects = useMemo(
    () => getCardDragPreviewRects(measuredRects, cardDragPreviews),
    [cardDragPreviews, measuredRects]
  );

  const renderedConnections = useMemo(
    () =>
      getRenderedConnections(cards, connections, dragPreviewRects).filter(({ linePoints }) =>
        rectsIntersect(getConnectionBounds(linePoints), renderWindow)
      ),
    [cards, connections, dragPreviewRects, renderWindow]
  );
  const preview = useMemo(
    () => getConnectionPreview(cards, dragPreviewRects, connectingFromId, connectionPointer),
    [cards, connectingFromId, connectionPointer, dragPreviewRects]
  );

  if (renderedConnections.length === 0 && !preview) {
    return null;
  }

  const selectConnection = (
    event: PointerEvent<SVGPolylineElement>,
    connection: WallConnection
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setCurrentElement({ id: connection.id, type: 'connection' });
    setSelectedIds([]);
  };

  return (
    <Box
      aria-hidden
      sx={{
        height: renderWindow.height,
        left: renderWindow.x,
        pointerEvents: 'none',
        position: 'absolute',
        top: renderWindow.y,
        width: renderWindow.width,
        zIndex: connectionLayerZIndex,
      }}
    >
      <svg
        height={renderWindow.height}
        width={renderWindow.width}
        style={{ display: 'block', overflow: 'hidden' }}
      >
        <g transform={`translate(${-renderWindow.x} ${-renderWindow.y})`}>
          {renderedConnections.map(({ connection, endCard, linePoints, startCard }) => {
            const points = getConnectorPoints(linePoints).join(' ');
            const selected =
              currentElement?.type === 'connection' && currentElement.id === connection.id;
            const isFocusFiltered = !isConnectionVisibleForParticipantFilter(
              startCard,
              endCard,
              visibleParticipantIds
            );

            return (
              <ConnectionPath
                key={connection.id}
                color={connection.color || '#000000'}
                linePoints={linePoints}
                onSelect={(event) => selectConnection(event, connection)}
                opacity={isFocusFiltered ? focusFilteredConnectionOpacity : 1}
                points={points}
                selected={selected}
              />
            );
          })}
          {preview && rectsIntersect(getConnectionBounds(preview.linePoints), renderWindow) && (
            <PreviewConnectionPath
              color={selectedPenColor}
              linePoints={preview.linePoints}
              points={
                preview.kind === 'card'
                  ? getConnectorPoints(preview.linePoints).join(' ')
                  : getPointConnectorPoints(preview.linePoints).join(' ')
              }
            />
          )}
        </g>
      </svg>
    </Box>
  );
}

function getConnectionBounds({ endPoint, startPoint }: ConnectionLinePoints): BoardRect {
  const x = Math.min(startPoint.x, endPoint.x) - 32;
  const y = Math.min(startPoint.y, endPoint.y) - 32;

  return {
    height: Math.abs(endPoint.y - startPoint.y) + 64,
    width: Math.abs(endPoint.x - startPoint.x) + 64,
    x,
    y,
  };
}

type ConnectionPreview =
  | { kind: 'card'; linePoints: ConnectionLinePoints }
  | { kind: 'point'; linePoints: ConnectionLinePoints };

function getConnectionPreview(
  cards: WallCard[],
  measuredRects: Record<string, BoardRect>,
  connectingFromId: BoardElementId | null,
  connectionPointer: { x: number; y: number } | null
): ConnectionPreview | null {
  if (!connectingFromId || !connectionPointer) {
    return null;
  }

  const cardIndex = indexCards(cards);
  const startCard = cardIndex.get(connectingFromId);

  if (!startCard) {
    return null;
  }

  const cardRects = getBoardCardRectMap(cards);
  const startRect = measuredRects[startCard.id] ?? cardRects.get(startCard.id);

  if (!startRect) {
    return null;
  }

  const targetCard = getPreviewTargetCard(
    cardIndex,
    cardRects,
    measuredRects,
    connectingFromId,
    connectionPointer
  );

  if (targetCard) {
    const targetRect = measuredRects[targetCard.id] ?? cardRects.get(targetCard.id);

    if (targetRect) {
      return {
        kind: 'card',
        linePoints: getConnectionLinePoints(startCard, startRect, targetCard, targetRect),
      };
    }
  }

  return {
    kind: 'point',
    linePoints: getConnectionPointLinePoints(startCard, startRect, connectionPointer),
  };
}

function getPreviewTargetCard(
  cardIndex: Map<string, WallCard>,
  cardRects: Map<string, BoardRect>,
  measuredRects: Record<string, BoardRect>,
  connectingFromId: BoardElementId,
  connectionPointer: { x: number; y: number }
) {
  const orderedCards = [...cardIndex.values()].sort((a, b) => (b.zPos ?? 0) - (a.zPos ?? 0));

  return (
    orderedCards.find((card) => {
      if (card.id === connectingFromId) {
        return false;
      }

      const rect = measuredRects[card.id] ?? cardRects.get(card.id);

      return Boolean(rect && pointInRect(connectionPointer, rect));
    }) ?? null
  );
}

function pointInRect(
  point: { x: number; y: number },
  rect: { height: number; width: number; x: number; y: number }
) {
  return (
    point.x > rect.x &&
    point.x < rect.x + rect.width &&
    point.y > rect.y &&
    point.y < rect.y + rect.height
  );
}

function indexCards(cards: WallCard[], cardIndex = new Map<string, WallCard>()) {
  cards.forEach((card) => {
    cardIndex.set(card.id, card);
    indexCards(card.cards ?? [], cardIndex);
  });

  return cardIndex;
}

interface ConnectionPathProps {
  color: string;
  linePoints: ConnectionLinePoints;
  onSelect: (event: PointerEvent<SVGPolylineElement>) => void;
  opacity: number;
  points: string;
  selected: boolean;
}

function ConnectionPath({
  color,
  linePoints,
  onSelect,
  opacity,
  points,
  selected,
}: ConnectionPathProps) {
  return (
    <g opacity={opacity} style={{ transition: 'opacity 120ms ease' }}>
      {selected && (
        <polyline
          fill="none"
          points={points}
          stroke="#7dc9fb"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={6}
        />
      )}
      <polyline
        fill="none"
        points={points}
        stroke={color}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
      />
      <polyline
        aria-label="Connection"
        data-end-side={linePoints.endPoint.side}
        data-start-side={linePoints.startPoint.side}
        fill="none"
        onPointerDown={onSelect}
        points={points}
        pointerEvents="stroke"
        stroke="transparent"
        strokeWidth={16}
      />
    </g>
  );
}

function PreviewConnectionPath({
  color,
  linePoints,
  points,
}: {
  color: string;
  linePoints: ConnectionLinePoints;
  points: string;
}) {
  return (
    <polyline
      aria-hidden
      data-end-side={linePoints.endPoint.side}
      data-start-side={linePoints.startPoint.side}
      fill="none"
      points={points}
      pointerEvents="none"
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
    />
  );
}
